import { app } from '@services/firebase';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import axios from 'axios';
import { types, Instance, getRoot } from 'mobx-state-tree';
import { callAgroApi } from '@services/agroapi';
import { User } from './User';
import { OrganizationResponse, UserResponse } from '@services/responseTypes';
import { Transformers } from '@services/transformer';
import { IRootStoreAny } from './rootUtilType';
import Cookies from 'js-cookie';
export const UserStore = types.model('UserStore', {
  user: User,
  firebaseUid: types.maybe(types.string),
  firebaseToken: types.maybe(types.string),
  agroApiToken: types.maybe(types.model({
    // https://datatracker.ietf.org/doc/html/rfc6749#section-4.2.2
    access_token: types.string,
    token_type: types.string,
    expires_in: types.number,
    expires_at: types.number,
    created_at: types.number
  }))
}).views(self => ({
  get isLoggedIn() {
    return self.agroApiToken?.access_token !== undefined;
  },
  get isLF() {
    return self.firebaseUid === 'W5y1SEVpi0gQ9oUN56psTnM4LK82' || self.firebaseUid === 'LhzfMyfSBBQuAIlC0817DfPz2k53';
  },
  get root() {
    return getRoot<IRootStoreAny>(self);
  }
})).actions(self => ({
  setUser(fetchedUser) {
    self.user = fetchedUser;
  },
  fetchOrganizations({
    page = 1,
    items = 50,
    search
  }: {
    page?: number;
    items?: number;
    search?: string;
  } = {}) {
    let url = `/organizations?page=${page}&items=${items}`;
    if (search) {
      url += `&filter[query]=${encodeURIComponent(search)}`;
    }
    return callAgroApi<OrganizationResponse[]>(url, 'GET').then(({
      data
    }) => data.map(x => Transformers.organization(x))).then(organizations => self.root.organizationsStore.pushOrganizations(organizations));
  },
  fetchOrganizationById(id: string) {
    return callAgroApi<OrganizationResponse>(`/organizations/${id}`, 'GET').then(({
      data
    }) => Transformers.organization(data)).then(organization => {
      self.root.organizationsStore.pushOrganizations([organization]);
      return organization;
    });
  },
  setAgroApiToken(agroApiToken: {
    access_token: string;
    token_type: string;
    expires_in: number;
    expires_at: number;
    created_at: number;
  }) {
    self.agroApiToken = agroApiToken;
  },
  setFirebaseToken(firebaseToken: string) {
    self.firebaseToken = firebaseToken;
  },
  setFirebaseUid(uid: string) {
    self.firebaseUid = uid;
  },
  createUser() {
    if (!self.firebaseToken) {
      self.agroApiToken = {
        access_token: '',
        token_type: '',
        expires_in: 0,
        expires_at: 0,
        created_at: 0
      };
      return;
    }
    return axios.post(`${process.env.NEXT_PUBLIC_AGROAPI_URL}/users`, {}, {
      headers: {
        Authorization: `Bearer ${self.firebaseToken}`,
        'X-AGROAPI-VERSION': '1.51.0'
      }
    }).then(response => {
      this.setAgroApiToken({
        access_token: response.data.token.access_token,
        token_type: response.data.token.token_type,
        expires_in: response.data.token.expires_in,
        expires_at: response.data.token.expires_in * 1000 + Date.now(),
        created_at: response.data.token.created_at
      });
      return self.agroApiToken;
    });
  },
  getAgroApiToken() {
    if (!self.firebaseToken) {
      self.agroApiToken = {
        access_token: '',
        token_type: '',
        expires_in: 0,
        expires_at: 0,
        created_at: 0
      };
      return;
    }
    return axios.post(`${process.env.NEXT_PUBLIC_AGROAPI_URL}/users/login`, {}, {
      headers: {
        Authorization: `Bearer ${self.firebaseToken}`,
        'X-AGROAPI-VERSION': '1.51.0'
      }
    }).then(response => {
      this.setAgroApiToken({
        access_token: response.data.token.access_token,
        token_type: response.data.token.token_type,
        expires_in: response.data.token.expires_in,
        expires_at: response.data.token.expires_in * 1000 + Date.now(),
        created_at: response.data.token.created_at
      });
      return self.agroApiToken;
    });
  },
  confirmEmail() {
    return axios.post(`/api/confirmEmail`, {}, {
      headers: {
        Authorization: `Bearer ${self.firebaseToken}`
      }
    });
  },
  loginWithJwt(jwt: string) {
    const auth = getAuth(app);
    return signInWithCustomToken(auth, jwt as string).then(userCred => userCred.user.getIdToken()).then(tok => this.setFirebaseToken(tok));
  },
  logout() {
    const auth = getAuth(app);
    return signOut(auth).then(() => {
      Cookies.remove('login_method', {
        sameSite: 'strict',
        secure: true
      });
      this.setFirebaseToken('');
      this.setAgroApiToken({
        access_token: '',
        token_type: '',
        expires_in: 0,
        expires_at: 0,
        created_at: 0
      });
    });
  }
})).actions(self => ({
  initialize() {
    if (process.env.NEXT_PUBLIC_RUN_ENV === 'test') {
      self.setAgroApiToken({
        access_token: process.env.NEXT_PUBLIC_TEST_TOKEN as string,
        token_type: 'Bearer ',
        expires_in: 3600 * 1000,
        expires_at: Date.now() + 3600 * 1000,
        created_at: Date.now()
      });
      return self.fetchOrganizations().then(() => {
        const storedOrgId = localStorage.getItem('currentOrganizationId');
        if (storedOrgId) {
          const existingOrg = self.root.organizationsStore.organizations.find(org => org.id === storedOrgId);
          if (!existingOrg) {
            return self.fetchOrganizationById(storedOrgId);
          }
        }
      });
    }
    return self.getAgroApiToken()?.catch(error => {
      if (error?.response?.status == 404) {
        return self.createUser();
      }
      throw error;
    }).then(() => self.fetchOrganizations()).then(() => {
      const storedOrgId = localStorage.getItem('currentOrganizationId');
      if (storedOrgId) {
        const existingOrg = self.root.organizationsStore.organizations.find(org => org.id === storedOrgId);
        if (!existingOrg) {
          return self.fetchOrganizationById(storedOrgId);
        }
      }
    });
  },
  fetchUser() {
    return callAgroApi<UserResponse>(`/user`, 'GET').then(({
      data
    }) => {
      self.setUser(Transformers.user(data));
    });
  }
}));
export type IUserStore = Instance<typeof UserStore>;
export const userStore = UserStore.create({
  user: {
    id: '',
    name: '',
    avatar: '',
    email: '',
    settings: {
      currency: '',
      lang: '',
      area_unit: '',
      weight_unit: '',
      soil_units: {}
    }
  }
});